import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState } from "@/components/EmptyState";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

import { confirmAction } from "@/lib/dialogs";
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const qty = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
const dmy = (d) => (d ? d.split("-").reverse().join("-") : "—");

const STATUS_CLS = {
  REQUESTED: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400",
  ACCEPTED: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-400",
  RECEIVED: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  REJECTED: "border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-400",
  CANCELLED: "border-border bg-muted text-muted-foreground",
};
const STATUS_TEXT = { REQUESTED: "Requested", ACCEPTED: "Accepted - waiting to be received", RECEIVED: "Received", REJECTED: "Rejected", CANCELLED: "Cancelled" };

export function StatusChip({ status }) {
  return <Badge variant="outline" className={`gap-1.5 font-medium ${STATUS_CLS[status] || STATUS_CLS.CANCELLED}`}><span className="inline-block h-1.5 w-1.5 rounded-full bg-current" />{STATUS_TEXT[status] || status}</Badge>;
}

function Box({ title, hint, children }) {
  return (
    <div className="space-y-2 p-4">
      <div><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>{hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}</div>
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ presentational (data in, events out) */
export function BoardTable({ rows, onRequest }) {
  if (!rows.length) return <div className="p-4"><EmptyState title="No surplus offered by any site right now" description="When a site has material left over it can offer it from Stock at site → Offer to sites." /></div>;
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Material</TableHead><TableHead className="text-right">Available</TableHead><TableHead className="text-right">Unit cost</TableHead>
          <TableHead>From site</TableHead><TableHead>Offered</TableHead><TableHead>Note</TableHead><TableHead />
        </TableRow></TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} data-testid={`surplus-offer-${r.id}`}>
              <TableCell className="text-xs"><div className="font-medium">{r.item_name}</div><div className="font-mono text-[11px] text-muted-foreground">{r.item_code}</div></TableCell>
              <TableCell className="text-right font-mono text-xs">{qty(r.remaining)} {r.uom || ""}</TableCell>
              <TableCell className="text-right font-mono text-xs">{inr(r.unit_cost)}</TableCell>
              <TableCell className="text-xs"><div>{r.project}</div><div className="text-[11px] text-muted-foreground">{r.store}</div></TableCell>
              <TableCell className="text-xs">{dmy(r.offered_on)}</TableCell>
              <TableCell className="max-w-[16rem] text-xs text-muted-foreground">{r.note || "—"}</TableCell>
              <TableCell className="text-right">
                {r.mine ? <Badge variant="secondary">Your site's offer</Badge>
                  : r.blocked ? <span className="text-[11px] text-muted-foreground">{r.blocked}</span>
                  : <Button size="sm" variant="outline" onClick={() => onRequest(r)} data-testid={`surplus-request-${r.id}`}>Request</Button>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ReqTable({ rows, who, actions }) {
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Material</TableHead><TableHead className="text-right">Qty</TableHead><TableHead>{who}</TableHead><TableHead>Status</TableHead><TableHead>GRN</TableHead><TableHead />
        </TableRow></TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} data-testid={`surplus-request-row-${r.id}`}>
              <TableCell className="text-xs"><div className="font-medium">{r.item_name}</div><div className="text-[11px] text-muted-foreground">{dmy(r.requested_on)}{r.note ? ` · ${r.note}` : ""}</div></TableCell>
              <TableCell className="text-right font-mono text-xs">{qty(r.status === "RECEIVED" ? r.received_qty : r.quantity)} {r.uom || ""}</TableCell>
              <TableCell className="text-xs">{who === "Requested by" ? r.to_project : r.from_project}</TableCell>
              <TableCell className="text-xs"><StatusChip status={r.status} />{r.reason && <div className="mt-1 text-[11px] text-muted-foreground">{r.reason}</div>}</TableCell>
              <TableCell className="font-mono text-xs">{r.grn_number ? <div>{r.grn_number}<div className="text-[11px] text-muted-foreground">{dmy(r.received_on)}</div></div> : "—"}</TableCell>
              <TableCell className="text-right"><span className="inline-flex gap-2">{actions(r)}</span></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function RequestsView({ data, onAccept, onReject, onReceive, onCancelRequest, onWithdraw }) {
  const { offers, incoming, outgoing } = data;
  return (
    <div className="divide-y divide-border">
      <Box title="Requests for your site's surplus" hint="You accept or reject. Accepting holds the quantity; the stock only moves when the other site confirms receipt.">
        {incoming.length === 0 ? <p className="text-xs text-muted-foreground">No requests yet.</p> : (
          <ReqTable rows={incoming} who="Requested by" actions={(r) => r.status === "REQUESTED" ? (<>
            <Button size="sm" onClick={() => onAccept(r)} data-testid={`surplus-accept-${r.id}`}>Accept</Button>
            <Button size="sm" variant="outline" onClick={() => onReject(r)} data-testid={`surplus-reject-${r.id}`}>Reject</Button></>) : null} />
        )}
      </Box>
      <Box title="Your requests to other sites" hint="Once a request is accepted and the material arrives, confirm it here with a Transfer GRN.">
        {outgoing.length === 0 ? <p className="text-xs text-muted-foreground">You have not requested anything. Check the Surplus board before raising a purchase requisition.</p> : (
          <ReqTable rows={outgoing} who="From site" actions={(r) => (<>
            {r.status === "ACCEPTED" && <Button size="sm" onClick={() => onReceive(r)} data-testid={`surplus-receive-${r.id}`}>Receive (GRN)</Button>}
            {(r.status === "REQUESTED" || r.status === "ACCEPTED") && <Button size="sm" variant="outline" onClick={() => onCancelRequest(r)}>Cancel</Button>}</>)} />
        )}
      </Box>
      <Box title="Your open offers">
        {offers.length === 0 ? <p className="text-xs text-muted-foreground">Nothing on offer. Use "Offer to sites" on a row in Stock at site.</p> : (
          <div className="overflow-x-auto rounded-md border border-border">
            <Table>
              <TableHeader><TableRow><TableHead>Material</TableHead><TableHead className="text-right">Offered</TableHead><TableHead className="text-right">Still available</TableHead><TableHead>Note</TableHead><TableHead /></TableRow></TableHeader>
              <TableBody>
                {offers.map((o) => (
                  <TableRow key={o.id}>
                    <TableCell className="text-xs font-medium">{o.item_name}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{qty(o.quantity)} {o.uom || ""}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{qty(o.remaining)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{o.note || "—"}</TableCell>
                    <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => onWithdraw(o)} data-testid={`surplus-withdraw-${o.id}`}>Withdraw</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Box>
    </div>
  );
}

/* ------------------------------------------------------------------ dialogs */
function FormDialog({ open, title, onClose, onSubmit, saving, disabled, submitLabel, children }) {
  return (
    <Dialog open={!!open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">{children}</div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={disabled || saving} onClick={onSubmit}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} {submitLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Summary = ({ children }) => <div className="rounded-md bg-muted/50 p-2.5 text-xs">{children}</div>;

export function OfferDialog({ row, projectId, onClose, onDone }) {
  const [quantity, setQuantity] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (row) { setQuantity(""); setNote(""); } }, [row]);
  const available = row ? Number(row.qty) : 0;
  const q = Number(quantity);
  const bad = !(q > 0) || q > available;
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/surplus/offers`, { warehouse_id: row.store_id, item_id: row.item_id, quantity: q, note: note || null });
      toast.success(`${qty(q)} ${row.item_name} offered to other sites`);
      onDone();
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setSaving(false); }
  };
  return (
    <FormDialog open={!!row} title="Offer to other sites" onClose={onClose} onSubmit={submit} saving={saving} disabled={bad} submitLabel="Post offer">
      {row && (<>
        <Summary><div className="font-medium text-foreground">{row.item_code} · {row.item_name}</div><div className="text-muted-foreground">In {row.store}: {qty(row.qty)} {row.uom || ""} at {inr(row.avg_cost)} each</div></Summary>
        <div><Label>Quantity to offer</Label><Input type="number" min="0" step="any" value={quantity} onChange={(e) => setQuantity(e.target.value)} className="mt-1.5" data-testid="offer-qty-input" />{q > available && <p className="mt-1 text-xs text-rose-500">More than the stock ({qty(row.qty)}).</p>}</div>
        <div><Label>Note (optional)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5" placeholder="Condition, where to collect, pick-up days" /></div>
        <p className="text-xs text-muted-foreground">Other sites will see this on the Surplus board. Nothing leaves your store until you accept a request and the other site confirms receipt.</p>
      </>)}
    </FormDialog>
  );
}

function RequestDialog({ row, projectId, onClose, onDone }) {
  const [quantity, setQuantity] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (row) { setQuantity(String(Number(row.remaining))); setNote(""); } }, [row]);
  const q = Number(quantity);
  const bad = !(q > 0) || (row && q > Number(row.remaining));
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/surplus/offers/${row.id}/request`, { quantity: q, note: note || null });
      toast.success(`Requested ${qty(q)} ${row.item_name} from ${row.project}`);
      onDone();
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setSaving(false); }
  };
  return (
    <FormDialog open={!!row} title="Request transfer" onClose={onClose} onSubmit={submit} saving={saving} disabled={bad} submitLabel="Send request">
      {row && (<>
        <Summary><div className="font-medium text-foreground">{row.item_code} · {row.item_name}</div><div className="text-muted-foreground">{row.project} has {qty(row.remaining)} {row.uom || ""} at {inr(row.unit_cost)} each</div></Summary>
        <div><Label>Quantity needed</Label><Input type="number" min="0" step="any" value={quantity} onChange={(e) => setQuantity(e.target.value)} className="mt-1.5" data-testid="request-qty-input" /></div>
        <div><Label>Note (optional)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5" placeholder="When you need it, who will collect" /></div>
        {!bad && <p className="text-xs text-muted-foreground">Value at their cost: {inr(q * Number(row.unit_cost))}. It reaches your store at this cost once you confirm receipt.</p>}
      </>)}
    </FormDialog>
  );
}

function RejectDialog({ row, projectId, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (row) setReason(""); }, [row]);
  const submit = async () => {
    setSaving(true);
    try { await api.post(`/projects/${projectId}/surplus/requests/${row.id}/reject`, { reason }); toast.success("Request rejected"); onDone(); }
    catch (err) { toast.error(getErrorMessage(err)); } finally { setSaving(false); }
  };
  return (
    <FormDialog open={!!row} title="Reject request" onClose={onClose} onSubmit={submit} saving={saving} disabled={!reason.trim()} submitLabel="Reject">
      {row && (<>
        <Summary>{row.to_project} asked for {qty(row.quantity)} {row.uom || ""} of {row.item_name}</Summary>
        <div><Label>Reason</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1.5" data-testid="reject-reason-input" placeholder="Needed for our own work, already promised..." /></div>
      </>)}
    </FormDialog>
  );
}

function ReceiveDialog({ row, projectId, onClose, onDone }) {
  const [received, setReceived] = useState("");
  const [date, setDate] = useState(todayISO());
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (row) { setReceived(String(Number(row.quantity))); setDate(todayISO()); setRemarks(""); } }, [row]);
  const q = Number(received);
  const bad = !(q > 0) || (row && q > Number(row.quantity));
  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/surplus/requests/${row.id}/receive`, { received_qty: q, received_on: date, remarks: remarks || null });
      toast.success(`Received ${qty(q)} ${row.item_name}. Transfer GRN ${data.grn_number}`);
      onDone();
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setSaving(false); }
  };
  return (
    <FormDialog open={!!row} title="Receive transfer (GRN)" onClose={onClose} onSubmit={submit} saving={saving} disabled={bad} submitLabel="Confirm receipt">
      {row && (<>
        <Summary><div className="font-medium text-foreground">{row.item_name}</div><div className="text-muted-foreground">From {row.from_project}. Accepted quantity {qty(row.quantity)} {row.uom || ""} at {inr(row.unit_cost)} each</div></Summary>
        <div><Label>Quantity actually received</Label><Input type="number" min="0" step="any" value={received} onChange={(e) => setReceived(e.target.value)} className="mt-1.5" data-testid="receive-qty-input" />{q > 0 && q < Number(row.quantity) && <p className="mt-1 text-xs text-amber-600">{qty(Number(row.quantity) - q)} short. It goes back on the other site's offer.</p>}</div>
        <div><Label>Received on</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" /></div>
        <div><Label>Remarks (optional)</Label><Input value={remarks} onChange={(e) => setRemarks(e.target.value)} className="mt-1.5" placeholder="Condition, damage, vehicle no." /></div>
        {!bad && <p className="text-xs text-muted-foreground">Stock in your site store increases by {qty(q)} and decreases at the sending site, at {inr(row.unit_cost)} each.</p>}
      </>)}
    </FormDialog>
  );
}

/* ------------------------------------------------------------------ containers (used by SiteInventory) */
export function SurplusBoardTab({ projectId, onChanged }) {
  const [rows, setRows] = useState(null);
  const [asking, setAsking] = useState(null);
  const load = useCallback(() => api.get("/surplus/board", { params: { project_id: projectId } }).then(({ data }) => setRows(data)).catch((err) => { setRows([]); toast.error(getErrorMessage(err)); }), [projectId]);
  useEffect(() => { load(); }, [load]);
  if (rows === null) return <div className="flex justify-center p-8"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  return (
    <>
      <BoardTable rows={rows} onRequest={setAsking} />
      <RequestDialog row={asking} projectId={projectId} onClose={() => setAsking(null)} onDone={() => { setAsking(null); load(); onChanged?.(); }} />
    </>
  );
}

export function SurplusRequestsTab({ projectId, onChanged }) {
  const [data, setData] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [receiving, setReceiving] = useState(null);
  const load = useCallback(() => api.get(`/projects/${projectId}/surplus/mine`).then(({ data: d }) => setData(d)).catch((err) => { setData({ offers: [], incoming: [], outgoing: [] }); toast.error(getErrorMessage(err)); }), [projectId]);
  useEffect(() => { load(); }, [load]);
  const act = async (url, okMsg, body) => {
    try { await api.post(`/projects/${projectId}/surplus/${url}`, body || {}); toast.success(okMsg); load(); onChanged?.(); }
    catch (err) { toast.error(getErrorMessage(err)); }
  };
  if (data === null) return <div className="flex justify-center p-8"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  const done = () => { setRejecting(null); setReceiving(null); load(); onChanged?.(); };
  return (
    <>
      <RequestsView data={data}
        onAccept={(r) => act(`requests/${r.id}/accept`, "Accepted. Waiting for the other site to receive it")}
        onReject={setRejecting} onReceive={setReceiving}
        onCancelRequest={async (r) => { if (await confirmAction("Cancel this request?")) act(`requests/${r.id}/cancel`, "Request cancelled"); }}
        onWithdraw={async (o) => { if (await confirmAction(`Withdraw the offer of ${o.item_name}? Pending requests will be rejected.`)) act(`offers/${o.id}/cancel`, "Offer withdrawn"); }} />
      <RejectDialog row={rejecting} projectId={projectId} onClose={() => setRejecting(null)} onDone={done} />
      <ReceiveDialog row={receiving} projectId={projectId} onClose={() => setReceiving(null)} onDone={done} />
    </>
  );
}
